/**
 * Geolocation Utility
 * 
 * Detects user's country to determine if web payment option should be shown.
 * Per App Store guidelines, external payment links are restricted in some regions.
 */

let _cachedCountryCode: string | null = null;

/**
 * Detect user's country code using multiple methods
 * @returns Two-letter country code (e.g., "US", "GB", "JP") or null if unable to detect
 */
export async function detectUserCountry(): Promise<string | null> {
  // Return cached result if available
  if (_cachedCountryCode !== null) {
    return _cachedCountryCode;
  }

  try {
    // Method 1: Try Intl API (most reliable, but requires locale data)
    const locale = Intl.DateTimeFormat().resolvedOptions().locale;
    if (locale) {
      const parts = locale.split('-');
      if (parts.length > 1) {
        const countryCode = parts[parts.length - 1].toUpperCase();
        if (countryCode.length === 2) {
          console.log('[Geo] Country detected via Intl API:', countryCode);
          _cachedCountryCode = countryCode;
          return countryCode;
        }
      }
    }
  } catch (error) {
    console.log('[Geo] Intl API detection failed:', error);
  }

  try {
    // Method 2: Try timezone-based detection
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezone) {
      // Map common US timezones
      const usTimezones = [
        'America/New_York',
        'America/Chicago', 
        'America/Denver',
        'America/Los_Angeles',
        'America/Phoenix',
        'America/Anchorage',
        'Pacific/Honolulu',
      ];
      
      if (usTimezones.some(tz => timezone.startsWith(tz.split('/')[0]))) {
        console.log('[Geo] US timezone detected:', timezone);
        _cachedCountryCode = 'US';
        return 'US';
      }
    }
  } catch (error) {
    console.log('[Geo] Timezone detection failed:', error);
  }

  try {
    // Method 3: Try IP-based geolocation API (as fallback)
    const response = await fetch('https://ipapi.co/json/', {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
    });

    if (response.ok) {
      const data = await response.json();
      const countryCode = data.country_code;
      if (countryCode && typeof countryCode === 'string') {
        console.log('[Geo] Country detected via IP API:', countryCode);
        _cachedCountryCode = countryCode;
        return countryCode;
      }
    }
  } catch (error) {
    console.log('[Geo] IP API detection failed:', error);
  }

  // Unable to detect - default to null (will show web option to be safe)
  console.log('[Geo] Unable to detect country, defaulting to null');
  return null;
}

/**
 * Check if user is in the United States
 * @returns true if in US or unable to detect (safe default), false otherwise
 */
export async function isUserInUS(): Promise<boolean> {
  const countryCode = await detectUserCountry();
  
  // If unable to detect, default to true (allow web option)
  // This is a safe default to avoid blocking legitimate US users
  if (countryCode === null) {
    console.log('[Geo] Unable to detect location, allowing web option (safe default)');
    return true;
  }
  
  const isUS = countryCode === 'US';
  console.log('[Geo] User in US:', isUS);
  return isUS;
}

/**
 * Check if web payment option should be shown on iOS native app
 * @returns true if web option should be shown
 */
export async function shouldShowWebPaymentOption(): Promise<boolean> {
  // Always show web option if not on native iOS
  if (typeof window === 'undefined') {
    return true;
  }

  // Check if in native app
  const isNative = !!(
    window.NativelyInfo ||
    window.NativelyHealth ||
    window.NativelyNotifications ||
    window.NativelyPurchases ||
    window.natively
  );

  // If not native, always show web option
  if (!isNative) {
    return true;
  }

  // On native iOS, only show web option if in US
  const inUS = await isUserInUS();
  console.log('[Geo] Show web payment option:', inUS);
  return inUS;
}
